import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { searchPlaces } from '../api.js';
import {
  debounce,
  isSearchable,
  nextHighlight,
  toSearchEntries,
  SEARCH_DEBOUNCE_MS,
  type SearchEntry,
} from './geosearch.js';

/**
 * Barre de recherche de lieu.
 *
 * Elle DEPLACE la carte, elle ne cree rien. Poser un point reste un geste
 * explicite (bouton « Ajouter un lieu », puis clic sur la carte) : creer
 * automatiquement le resultat choisi remplirait le vivier de brouillons a
 * chaque recherche exploratoire, et le mode ajout perdrait son sens.
 *
 * Toute la logique testable (anti-rebond, mise en forme, curseur) vit dans
 * `geosearch.ts` ; ce module n'est que l'etat et le balisage.
 */

type Status = 'idle' | 'loading' | 'done' | 'error';

export interface PlaceSearchProps {
  readonly onPick: (entry: SearchEntry) => void;
}

export function PlaceSearch({ onPick }: PlaceSearchProps): ReactNode {
  const [query, setQuery] = useState('');
  const [entries, setEntries] = useState<readonly SearchEntry[]>([]);
  const [status, setStatus] = useState<Status>('idle');
  const [highlight, setHighlight] = useState(-1);
  const [open, setOpen] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  // Numero de la derniere recherche lancee. Une reponse qui ne porte pas le
  // numero courant est arrivee en retard : l'afficher ferait clignoter la liste
  // avec les resultats d'une frappe deja abandonnee.
  const ticketRef = useRef(0);
  const listId = useId();

  const run = useCallback(async (text: string): Promise<void> => {
    ticketRef.current += 1;
    const ticket = ticketRef.current;
    try {
      const results = await searchPlaces(text.trim());
      if (ticket !== ticketRef.current) return;
      setEntries(toSearchEntries(results));
      setStatus('done');
      setHighlight(-1);
      setOpen(true);
    } catch {
      if (ticket !== ticketRef.current) return;
      setEntries([]);
      setStatus('error');
      setOpen(true);
    }
  }, []);

  const schedule = useMemo(
    () =>
      debounce((text: string) => {
        void run(text);
      }, SEARCH_DEBOUNCE_MS),
    [run],
  );

  // Sans cette annulation, une frappe suivie d'une fermeture de la fiche
  // declencherait une mise a jour d'etat sur un composant demonte.
  useEffect(() => () => schedule.cancel(), [schedule]);

  function reset(): void {
    schedule.cancel();
    // Invalide aussi les reponses encore en vol.
    ticketRef.current += 1;
    setEntries([]);
    setStatus('idle');
    setHighlight(-1);
    setOpen(false);
  }

  function change(value: string): void {
    setQuery(value);
    setHighlight(-1);
    if (!isSearchable(value)) {
      reset();
      return;
    }
    setStatus('loading');
    setOpen(true);
    schedule(value);
  }

  function choose(entry: SearchEntry): void {
    schedule.cancel();
    onPick(entry);
    setQuery(entry.primary);
    setOpen(false);
    setHighlight(-1);
    // Rendre le focus au document referme le clavier virtuel : sur telephone
    // il occupe la moitie de l'ecran, juste au moment ou l'on veut voir la
    // carte qui vient de bouger.
    inputRef.current?.blur();
  }

  function keyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      if (open) {
        setOpen(false);
        setHighlight(-1);
      } else {
        setQuery('');
        reset();
      }
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (entries.length === 0) return;
      event.preventDefault();
      setOpen(true);
      setHighlight((current) =>
        nextHighlight(current, entries.length, event.key === 'ArrowDown' ? 1 : -1),
      );
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      // Entree sans ligne visee prend la premiere : c'est presque toujours la
      // bonne, et cela evite d'exiger une fleche du bas avant de valider.
      const target = entries[highlight >= 0 ? highlight : 0];
      if (target) {
        choose(target);
        return;
      }
      // Entree pendant l'anti-rebond : on part tout de suite, sans attendre.
      if (isSearchable(query)) {
        schedule.cancel();
        setStatus('loading');
        void run(query);
      }
    }
  }

  // Le repli se decide au niveau du bloc et non de l'input : cliquer une ligne
  // fait perdre le focus au champ, et fermer sur ce seul evenement annulerait
  // le choix avant qu'il ne soit pris.
  function blur(event: FocusEvent<HTMLDivElement>): void {
    if (event.currentTarget.contains(event.relatedTarget)) return;
    setOpen(false);
    setHighlight(-1);
  }

  const showPanel = open && isSearchable(query);
  const empty = status === 'done' && entries.length === 0;

  return (
    <div className="geosearch" role="search" onBlur={blur}>
      <div className="geosearch__field">
        <svg className="geosearch__glass" viewBox="0 0 20 20" aria-hidden="true">
          <circle cx="8.5" cy="8.5" r="5.5" />
          <path d="M12.8 12.8L17 17" />
        </svg>

        <input
          ref={inputRef}
          className="geosearch__input"
          type="search"
          value={query}
          placeholder="Chercher un lieu"
          aria-label="Chercher un lieu et y deplacer la carte"
          role="combobox"
          aria-expanded={showPanel}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={highlight >= 0 ? `${listId}-${highlight}` : undefined}
          autoComplete="off"
          spellCheck={false}
          enterKeyHint="search"
          onChange={(event) => change(event.target.value)}
          onKeyDown={keyDown}
          onFocus={() => {
            if (entries.length > 0) setOpen(true);
          }}
        />

        {status === 'loading' ? <span className="geosearch__spinner" aria-hidden="true" /> : null}

        {query.length > 0 && status !== 'loading' ? (
          <button
            className="geosearch__clear"
            type="button"
            aria-label="Effacer la recherche"
            onClick={() => {
              setQuery('');
              reset();
              inputRef.current?.focus();
            }}
          >
            ×
          </button>
        ) : null}
      </div>

      {showPanel ? (
        <div className="geosearch__panel">
          <ul className="geosearch__list" id={listId} role="listbox" aria-label="Lieux trouves">
            {entries.map((entry, index) => (
              <li key={entry.key} role="presentation">
                <button
                  className={`geosearch__option${index === highlight ? ' is-active' : ''}`}
                  id={`${listId}-${index}`}
                  type="button"
                  role="option"
                  aria-selected={index === highlight}
                  onMouseEnter={() => setHighlight(index)}
                  onClick={() => choose(entry)}
                >
                  <span className="geosearch__name">{entry.primary}</span>
                  {entry.secondary.length > 0 ? (
                    <span className="geosearch__where">{entry.secondary}</span>
                  ) : null}
                  <span className="geosearch__kind">{entry.kind}</span>
                </button>
              </li>
            ))}
          </ul>

          {status === 'loading' ? (
            <p className="geosearch__note" role="status">
              Recherche en cours...
            </p>
          ) : null}

          {empty ? (
            <p className="geosearch__note" role="status">
              Aucun lieu ne correspond. Essaie une autre orthographe, ou ajoute le pays.
            </p>
          ) : null}

          {status === 'error' ? (
            <p className="geosearch__note geosearch__note--warn" role="status">
              La recherche est indisponible. Tu peux toujours naviguer sur la carte.
            </p>
          ) : null}

          {entries.length > 0 ? (
            <p className="geosearch__hint">
              La carte se deplace ; le lieu reste a poser avec « Ajouter un lieu ».
            </p>
          ) : null}
        </div>
      ) : null}

      <p className="visually-hidden" aria-live="polite">
        {status === 'loading'
          ? 'Recherche en cours'
          : empty
            ? 'Aucun lieu trouve'
            : entries.length > 0
              ? `${entries.length} lieux trouves`
              : ''}
      </p>
    </div>
  );
}
